import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/account/security")({
  component: AccountSecurity,
});

function AccountSecurity() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm && !saving;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) {
      if (/should be different|same/i.test(error.message)) {
        toast.error("Ο νέος κωδικός πρέπει να είναι διαφορετικός από τον προηγούμενο.");
      } else if (/weak|pwned|compromised/i.test(error.message)) {
        toast.error("Ο κωδικός είναι πολύ αδύναμος ή έχει διαρρεύσει. Διάλεξε άλλον.");
      } else {
        toast.error("Δεν ήταν δυνατή η αλλαγή κωδικού. Δοκίμασε ξανά.");
      }
      return;
    }
    setPassword("");
    setConfirm("");
    toast.success("Ο κωδικός σου άλλαξε με επιτυχία! 🐼");
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-1 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-bold">Ασφάλεια</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Άλλαξε τον κωδικό πρόσβασής σου. Δεν χρειάζεται να γνωρίζεις τον παλιό σου κωδικό.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Αλλαγή κωδικού</h3>
        </div>

        <form onSubmit={handleSubmit} className="max-w-sm space-y-4">
          <div>
            <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium">
              Νέος κωδικός
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Τουλάχιστον 8 χαρακτήρες"
                className="w-full rounded-xl border border-input bg-background px-3 py-2.5 pr-10 text-sm"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                aria-label={show ? "Απόκρυψη κωδικού" : "Εμφάνιση κωδικού"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {tooShort && (
              <p className="mt-1 text-xs text-destructive">Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.</p>
            )}
          </div>

          <div>
            <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-medium">
              Επιβεβαίωση νέου κωδικού
            </label>
            <input
              id="confirm-password"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="Πληκτρολόγησε ξανά τον κωδικό"
              className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
            />
            {mismatch && (
              <p className="mt-1 text-xs text-destructive">Οι κωδικοί δεν ταιριάζουν.</p>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {saving ? "Αποθήκευση…" : "Αλλαγή κωδικού"}
          </button>
        </form>
      </div>
    </div>
  );
}
